/**
 * Message List Component
 * Renders the list of chat messages with proper formatting
 */
import {
  Suspense,
  type ReactNode,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { isToolUIPart, type ChatStatus } from "ai";
import type { FeatureFlag } from "@/shared/feature-flags";
import type { MarkdownPerformanceEvent } from "@/shared/types/markdown-performance";
import type { EntityChatDataParts } from "@/shared/types/entity-chat-parts";
import type {
  EntityActionViewModel,
  EntityCardViewModel
} from "@/shared/types/entity-view";
import { shouldUseEntityRenderer } from "@/shared/entity-renderer-registry";
import type { SourceUrlPart } from "@/components/chat/renderers/MessageRow";
import { Spinner } from "@/components/ui/spinner";
import type { ChatUIMessage } from "@/shared/types/chat-ui-message";

const MessageRow = lazy(async () => {
  const mod = await import("@/components/chat/renderers/MessageRow");
  return { default: mod.MessageRow };
});

type EntityDataPartType = `data-${keyof EntityChatDataParts & string}`;
type EntityDataPart = {
  type: EntityDataPartType;
  data: unknown;
};

function isEntityDataPart(part: unknown): part is EntityDataPart {
  if (!part || typeof part !== "object") {
    return false;
  }

  const candidate = part as { type?: unknown };
  return (
    typeof candidate.type === "string" &&
    candidate.type.startsWith("data-entity")
  );
}

function isSourceUrlPart(part: unknown): part is SourceUrlPart {
  if (!part || typeof part !== "object") {
    return false;
  }

  const candidate = part as { type?: unknown; url?: unknown };
  return candidate.type === "source-url" && typeof candidate.url === "string";
}

function messageHasRichEntityUi(message: ChatUIMessage): boolean {
  return Boolean(
    message.parts?.some((part) => {
      if (isEntityDataPart(part)) {
        return part.type === "data-entityCards" || part.type === "data-entitySummary";
      }

      if (!isToolUIPart(part)) {
        return false;
      }

      return shouldUseEntityRenderer(part.type, part.output, part.errorText);
    })
  );
}

interface LazyMessageMountProps {
  children: ReactNode;
}

function LazyMessageMount({ children }: LazyMessageMountProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (hasMounted) {
      return;
    }

    const node = containerRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasMounted(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "900px",
        threshold: 0
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMounted]);

  return (
    <div
      ref={containerRef}
      style={{
        contentVisibility: "auto",
        containIntrinsicSize: "360px"
      }}
    >
      {hasMounted ? children : <div className="min-h-[96px]" />}
    </div>
  );
}

interface MessageListProps {
  /** Array of messages to display */
  messages: ChatUIMessage[];
  /** Current agent status */
  status: ChatStatus;
  /** Callback to regenerate the last response */
  onRegenerate: () => void;
  /** Function to check if a feature is enabled */
  isEnabled: (flagId: FeatureFlag) => boolean;
  /** Callback when an entity card is selected */
  onSelectEntity?: (entity: EntityCardViewModel) => void;
  /** Callback when an entity action is triggered */
  onRunEntityAction?: (
    action: EntityActionViewModel,
    context: { entity?: EntityCardViewModel; feedItemId?: number; claimId?: number }
  ) => void;
  onToolApproval?: (approvalId: string, approved: boolean) => void;
  onActivityMentionClick?: (
    contactId: number,
    context: { feedItemId?: number; claimId?: number }
  ) => void;
  canOpenActivityMentions?: boolean;
  /** Optional markdown performance event hook */
  onMarkdownPerformanceEvent?: (event: MarkdownPerformanceEvent) => void;
}

type MessageView = {
  hasRichEntityUi: boolean;
  sourceUrlParts: SourceUrlPart[];
  suppressNarrativeAfterRichEntityUi: boolean;
};

/**
 * Renders a list of chat messages with support for:
 * - Text messages
 * - Reasoning blocks
 * - Tool invocations
 * - Sources/citations
 * - Message actions (copy, retry)
 */
export function MessageList({
  messages,
  status,
  onRegenerate,
  isEnabled,
  onSelectEntity,
  onRunEntityAction,
  onToolApproval,
  onActivityMentionClick,
  canOpenActivityMentions = false,
  onMarkdownPerformanceEvent
}: MessageListProps) {
  const showToolCallDetails = isEnabled("tool-call-details");

  const messageView = useMemo<MessageView[]>(() => {
    const richUiByIndex = messages.map((message) => messageHasRichEntityUi(message));

    return messages.map((message, index) => {
      const hasRichEntityUi = richUiByIndex[index] ?? false;
      const previousHasRichEntityUi = richUiByIndex[index - 1] ?? false;
      const previousMessage = messages[index - 1];

      const suppressNarrativeAfterRichEntityUi =
        message.role === "assistant" &&
        !hasRichEntityUi &&
        previousMessage?.role === "assistant" &&
        previousHasRichEntityUi;

      const sourceUrlParts =
        message.role === "assistant"
          ? (message.parts?.filter((part) => isSourceUrlPart(part)) ?? [])
          : [];

      return {
        hasRichEntityUi,
        sourceUrlParts,
        suppressNarrativeAfterRichEntityUi
      };
    });
  }, [messages]);
  const lastMessageId = messages[messages.length - 1]?.id;

  return (
    <>
      {messages.map((message, messageIndex) => {
        const currentMessageView = messageView[messageIndex];
        if (!currentMessageView) {
          return null;
        }

        return (
          <LazyMessageMount key={`${message.id}-${messageIndex}`}>
            <Suspense fallback={<div className="min-h-[96px]" />}>
              <MessageRow
                message={message}
                isLatestMessage={message.id === lastMessageId}
                status={status}
                showToolCallDetails={showToolCallDetails}
                hasRichEntityUi={currentMessageView.hasRichEntityUi}
                suppressNarrativeAfterRichEntityUi={
                  currentMessageView.suppressNarrativeAfterRichEntityUi
                }
                sourceUrlParts={currentMessageView.sourceUrlParts}
                onRegenerate={onRegenerate}
                {...(onToolApproval ? { onToolApproval } : {})}
                canOpenActivityMentions={canOpenActivityMentions}
                {...(onActivityMentionClick ? { onActivityMentionClick } : {})}
                {...(onMarkdownPerformanceEvent
                  ? { onMarkdownPerformanceEvent }
                  : {})}
                {...(onRunEntityAction ? { onRunEntityAction } : {})}
                {...(onSelectEntity ? { onSelectEntity } : {})}
              />
            </Suspense>
          </LazyMessageMount>
        );
      })}

      {status === "submitted" && <Spinner />}
    </>
  );
}
